import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { userKeys } from '@/api/keys';

// Types
export interface UpdateUserRequest {
  name?: string;
  firstName?: string;
  lastName?: string;
  location?: string;
  bio?: string;
  photoUrl?: string;
  memLevel?: number;
  shareEmailOnProfile?: boolean;
}

/**
 * Hook to update the current user's profile
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData: UpdateUserRequest) =>
      apiFetch<void>('/users/', {
        method: 'PUT',
        body: userData,
      }),
    onSuccess: () => {
      // Invalidate the current user query to refetch fresh data
      queryClient.invalidateQueries({ queryKey: userKeys.current() });
    },
  });
}

export interface DeleteAccountRequest {
  password: string;
}

/**
 * Hook to permanently delete the authenticated user's account.
 * On success, call clearAuth() and queryClient.clear() before navigating to /login.
 * Auth teardown and navigation are handled in the component's onSuccess callback
 * to keep this hook transport-only — matching the useLogout pattern in useAuthMutations.ts.
 */
export function useDeleteAccount() {
  return useMutation({
    mutationFn: (data: DeleteAccountRequest) =>
      apiFetch<void>('/users/delete_account/', {
        method: 'POST',
        body: data,
      }),
  });
}
